import { QueryResult } from "@apollo/client";
import api from "csi/api";
import { CSS_TABLE_COLUMNS } from "csi/constants";
import { Context } from "csi/def/Context";
import Heading from "csi/def/Heading";
import ActionData from "csi/heading/ActionData";
import Renderer from "csi/heading/Renderer";
import Spec from "csi/heading/Spec";
import html from "csi/html";
import csi from "csi/main";
import Nodes from "csi/store/Nodes";
import { CSS_HEADER_CENTER } from "csi/table";
import PathMatrix from "csi/table/matrix/Path";
import TableWidget from "csi/table/Widget";
import types from "csi/types";
import { C8Node } from "csi/typings";
import { QueryLevel } from "csi/view/_query/Level";
import MaterialIcon from "csi/widget/MaterialIcon";
import xml from "csi/xml";
import declare from "dojo/_base/declare";
import _ from "underscore";

const RANGE_TABLE_DEFAULT_WIDTH = 48;

const _Renderer = declare(Renderer, {
	/**
	 * @override
	 * Render checkmark if size in context is selected, and not-applicable if size is not in the size range
	 */
	toHtmlStatic: function(ctx: Context, h: Heading, refNode, actionData: ActionData) {
		const node = ctx.getNode();

		// if rendering first cell, need to ensure it is free of any content for check to be centered
		html.clear(refNode);

		if (!node) {
			// size not in size range
			refNode.classList.add(html.CSS_NOT_APPLICABLE);
		} else if ((ctx.view as any).selectedSizes.has(node.$URL)) {
			// size is selected
			new MaterialIcon({
				icon: "check",
			}).placeAt(refNode);

			return true;
		}

		return false;
	},
});

const _SizesMatrix = declare(PathMatrix, {
	bAllowOnRootNode: true,
	hideLoneSubheader: true,

	/**
	 * @override
	 * Return column specs for 1d or composite size range
	 */
	getColumnSpecs: function(ctx: Context, h: Heading) {
		const { dim1Urls, sizeSet } = (ctx.view as any);
		const columnUrls = dim1Urls.length ? dim1Urls : sizeSet.dim1SizeUrls;
		const sh = h.m_subheadings[0];
		const specs = [];

		columnUrls.forEach((columnUrl) => {
			const subspec = new Spec({
				heading: sh,
			});

			// for composite size ranges, there will be empty urls in between sub size ranges
			if (columnUrl === csi.EMPTY_URL) {
				subspec.getColumnWidth = (): number => types.COLUMN_WIDTH_MIN;
			}

			const spec = new Spec({
				headerParams: {
					className: CSS_TABLE_COLUMNS,
					innerText: sizeSet.getSizeName(columnUrl),
				},
				heading: h,
				subs: [subspec],
			});

			spec.setParam("columnUrl", columnUrl, true);
			specs.push(spec);
		});

		return specs;
	},

	/**
	 * @override
	 * Return the given node (size range)
	 */
	_getRowNodes: function(ctx: Context, qr: QueryResult, c: Spec, node: Node) {
		return [node];
	},

	/**
	 * @override
	 * Return the column size node
	 */
	_getSpecNodes: function(ctx: Context, spec: Spec, nodes: Node[]) {
		return [ctx.cache.get(spec.columnUrl)];
	},
});

const _Sizes2dMatrix = declare(PathMatrix, {
	/**
	 * @override
	 * Return column specs 2d size range
	 */
	getColumnSpecs: function(ctx: Context, h: Heading) {
		const { sizeSet } = (ctx.view as any);
		const columnUrls = sizeSet.dim1SizeUrls;
		const sh = h.m_subheadings[0];

		const subs = columnUrls.map((columnUrl) => {
			return new Spec({
				columnUrl,
				headerParams: {
					className: CSS_TABLE_COLUMNS,
					innerText: sizeSet.getSizeName(columnUrl),
				},
				heading: sh,
			});
		});

		const matrixSpec = new Spec({
			headerParams: {
				className: CSS_TABLE_COLUMNS,
				colSpan: subs.length,
				innerText: sizeSet.dim1Name,
			},
			heading: h,
			subs,
		});

		return [matrixSpec];
	},

	/**
	 * @override
	 * Just return the given node (size range or 2nd dimension size) as the heading onDraw function will determine the rest
	 */
	_getRowNodes: function(ctx: Context, qr: QueryResult, c: Spec, node: Node) {
		return [node];
	},

	/**
	 * @override
	 * Just return the given node (size range or 2nd dimension size) as the heading onDraw function will determine the rest
	 */
	_getSpecNodes: function(ctx: Context, spec: Spec, nodes: C8Node[]) {
		const { cache, view } = ctx;
		const { sizeSet } = (view as any);
		const size1Url = spec.columnUrl;
		const size2Url = nodes[0].$URL;
		const sizeUrl = sizeSet.get2dSizeUrl(size1Url, size2Url);
		return [cache.get(sizeUrl)];
	},
});

const SizesInRangeTable = declare(TableWidget, {
	class: "csi-sizes-table",
	hideExtraColumn: true,
	hideFooter: true,
	hideScrollbar: true,

	/** @property {string[]} dim1Urls - urls to show across table columns - basically the same as what is in the size set, 
	 * but has empty urls in between sub size ranges for composite size ranges */
	dim1Urls: null,

	/** @property {Set} selectedSizes - set containing sizes that are selected from the range */
	selectedSizes: null,

	/** @property {SizeSet} sizeSet - size set for size range */
	sizeSet: null,

	/**
	 * @override
	 * Always return 1 as in the 1d case we use the root level and not the main level
	 */
	getNumberOfResultLevels: function(queryLevels: QueryLevel[]) {
		return 1;
	},

	/**
	 * @override
	 * If scrollbar is hidden and view does not fit, then add scrollbar
	 */
	onViewEnd() {
		const {
			headingsNode: { offsetHeight, scrollHeight },
			hideFooter,
			hideScrollbar,
			scrollData,
		} = this;

		if ((hideFooter && scrollData.isFrozen()) || (hideScrollbar && (scrollHeight > offsetHeight))) {
			this.hideFooter = false;
			this.hideScrollbar = false;
			this.redraw();
		}
	},

	/**
	 * @override
	 * Set view options based on given size range and sizes
	 */
	_resolveView: function() {
		const { cacheView, defView, sizeSet } = this;

		// RequestGenerator can load view without sizeSet, so must check for that
		if (sizeSet) {
			const is2d = sizeSet.has2dSizes();
			let storeUrls;

			if (is2d) {
				this.set("tableHideRoot", true);
				storeUrls = sizeSet.dim2SizeUrls;
			}

			this.set("store", new Nodes({
				cachingStore: cacheView,
				data: cacheView.getNodes(storeUrls),
			}));

			// name heading will only be visible for 2d size range; otherwise, only matrix will appear
			const nameHeading = _.extend(defView.newHeading(), {
				m_class: "csi-table-matrix-2nd-dimension",
				m_label: sizeSet.dim2Name || "",
				m_prop: xml.PROP_NAME,
				m_right: xml.RIGHT_NONE,
			});

			const matrixHeading = _.extend(defView.newHeading(), {
				m_name: "Matrix",
				matrix: {
					matrixClass: is2d ? _Sizes2dMatrix : _SizesMatrix,
				},
			});

			matrixHeading.addSubHeading(_.extend(defView.newHeading(), {
				m_class: CSS_HEADER_CENTER,
				m_name: "Check",
				classRenderer: _Renderer,
				columnWidth: api.getSizePopupColumnWidth() || RANGE_TABLE_DEFAULT_WIDTH,
			}));

			defView.m_headings = is2d ? [nameHeading, matrixHeading] : [matrixHeading];
		}

		this.inherited(arguments);
	},
});

export = SizesInRangeTable;
